import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import api, { getApiError } from '../lib/axios';
import { Star, Trash2, Loader2, Search, FolderOpen } from 'lucide-react';
import type { SavedListSummary } from '../types';

export default function SavedLists() {
  const navigate = useNavigate();
  const [lists, setLists] = useState<SavedListSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchLists = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/saved-lists/');
      setLists(res.data);
    } catch (err: any) {
      setError(getApiError(err, 'Failed to load saved lists'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLists(); }, []);

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this saved list?')) return;
    setDeletingId(id);
    try {
      await api.delete(`/api/saved-lists/${id}`);
      setLists(prev => prev.filter(l => l.id !== id));
    } catch (err: any) {
      setError(getApiError(err, 'Failed to delete list'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleLoadList = async (id: string) => {
    try {
      const res = await api.get(`/api/saved-lists/${id}`);
      const domains: string[] = res.data.domains;
      // Navigate to dashboard with domains pre-filled
      navigate('/dashboard', { state: { prefillDomains: domains.join('\n') } });
    } catch (err: any) {
      setError(getApiError(err, 'Failed to load list'));
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Saved Lists</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Your saved domain lists ({lists.length} total)
          </p>
        </div>

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-brand-600" />
          </div>
        ) : lists.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <FolderOpen className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">No saved lists yet</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Run a lookup and click the star icon to save your domain list.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="mt-4 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Start Searching
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {lists.map(list => (
              <div
                key={list.id}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex items-center justify-between hover:shadow-sm transition-shadow"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 bg-brand-50 dark:bg-brand-900/20 rounded-lg">
                    <Star className="w-5 h-5 text-brand-500" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-medium text-gray-900 dark:text-white truncate">{list.name}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {list.domain_count} domains &middot; {list.created_at ? new Date(list.created_at).toLocaleDateString() : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                  <button
                    onClick={() => handleLoadList(list.id)}
                    className="px-3 py-1.5 text-sm font-medium text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/20 hover:bg-brand-100 dark:hover:bg-brand-900/40 rounded-lg transition-colors flex items-center gap-1"
                  >
                    <Search className="w-3.5 h-3.5" /> Re-check
                  </button>
                  <button
                    onClick={() => handleDelete(list.id)}
                    disabled={deletingId === list.id}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    {deletingId === list.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
